import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import api from "../../../services/api";
import ReceivePOForm from "../components/ReceivePOForm";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { useTheme } from "@mui/material/styles";

const PurchaseOrderDetailPage: React.FC = () => {
  useProtectedRoute();
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<any>(null);
  const [showDelete, setShowDelete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Backend route: GET /api/inventory/po/:id
    api.get(`/inventory/po/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setOrder(res.data));
  }, [id, token]);

  const handleDelete = async () => {
    // Backend route: DELETE /api/inventory/po/:id
    await api.delete(`/inventory/po/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    navigate("/purchase-orders");
  };

  if (!order) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">PO #{order.orderNumber || order._id?.slice(-6)?.toUpperCase()}</h1>
      <div className={`p-6 rounded shadow mb-6 border ${dark ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-200'}`}>
        <p><b>Supplier:</b> {typeof order.supplier === 'string' ? order.supplier : (order.supplier?.name || '')}</p>
        <p><b>Status:</b> {order.status}</p>
        <p><b>Total:</b> ${(
          order.totalAmount ?? (Array.isArray(order.items) ? order.items.reduce((s: number, it: any) => s + (Number(it.unitPrice) || 0) * (Number(it.quantity) || 0), 0) : 0)
        ).toFixed(2)}</p>
        {/* Add more PO detail fields as required */}
      </div>
      {Array.isArray(order.items) && order.items.length > 0 && (
        <div className={`rounded shadow overflow-x-auto mb-6 border ${dark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
          <table className={`w-full ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
            <thead className={`${dark ? 'bg-slate-800' : 'bg-slate-50 text-slate-700'}`}>
              <tr>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2">Quantity</th>
                <th className="text-left px-3 py-2">Unit Price</th>
                <th className="text-left px-3 py-2">Subtotal</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${dark ? 'divide-slate-800' : 'divide-slate-200'}`}>
              {order.items.map((ln: any, i: number) => (
                <tr key={i}>
                  <td className="px-3 py-2">{typeof ln.item === 'string' ? ln.item : (ln.item?.name || '')} {ln.item?.sku ? `(${ln.item.sku})` : ''}</td>
                  <td className="px-3 py-2">{ln.quantity}</td>
                  <td className="px-3 py-2">${Number(ln.unitPrice || 0).toFixed(2)}</td>
                  <td className="px-3 py-2">${((Number(ln.quantity)||0) * (Number(ln.unitPrice)||0)).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ReceivePOForm po={order} />
      <div className="flex gap-4 mt-6">
        <button className="bg-yellow-700 text-white px-5 py-2 rounded" onClick={() => navigate(`/purchase-orders/${id}/edit`)}>Edit</button>
        <button className="bg-red-600 text-white px-5 py-2 rounded" onClick={() => setShowDelete(true)}>Delete</button>
      </div>
      {showDelete && <ConfirmDeleteModal onCancel={() => setShowDelete(false)} onConfirm={handleDelete} />}
    </div>
  );
};
export default PurchaseOrderDetailPage;
